import { useTranslation } from '@govard/i18n-core/react';
import { useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Alert, Button, Card, Form, Input, Segmented, Typography } from 'antd';

import { useAnalytics } from '@govard/analytics';

import { LanguageSwitcher } from '../components/layout/LanguageSwitcher.js';
import { AuthMark } from '../features/account/components/AuthMark.js';
import { ApiError, apiPost } from '../shared/api-client/index.js';
import { useScopeStore } from '../shared/scope/useCurrentScope.js';

const { Title, Text } = Typography;

interface LoginResponse {
  user: { id: string; email: string };
  defaultWorkspaceId: string;
}

type AuthMode = 'login' | 'register';

interface AuthFormValues {
  email: string;
  password: string;
  name?: string;
}

export default function LoginPage(): ReactElement {
  const navigate = useNavigate();
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const fetchMe = useScopeStore((state) => state.fetchMe);

  const [form] = Form.useForm<AuthFormValues>();
  const [mode, setMode] = useState<AuthMode>('login');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const onFinish = async (values: AuthFormValues): Promise<void> => {
    setSubmitting(true);
    setErrorMessage(null);
    try {
      if (mode === 'register') {
        await apiPost<LoginResponse, { email: string; password: string; name: string }>(
          '/auth/register',
          {
            email: values.email,
            password: values.password,
            name: values.name?.trim() ?? '',
          },
        );
      } else {
        await apiPost<LoginResponse, { email: string; password: string }>('/auth/login', {
          email: values.email,
          password: values.password,
        });
      }
      await fetchMe();

      const user = useScopeStore.getState().user;
      if (user === null) {
        setErrorMessage(t('crm.auth.login.session_error'));
        return;
      }

      analytics.track('feature_used', {
        feature: mode === 'register' ? 'auth.register' : 'auth.login',
        product: 'crm',
      });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'invalid_credentials') {
          setErrorMessage(t('crm.auth.login.invalid_credentials'));
        } else if (err.code === 'email_taken') {
          setErrorMessage(t('crm.auth.login.email_taken'));
        } else if (err.code === 'auth_rate_limited') {
          setErrorMessage(t('crm.auth.login.rate_limited'));
        } else if (err.code === 'dev_mode') {
          setErrorMessage(t('crm.auth.login.registration_unavailable'));
        } else {
          setErrorMessage(t('crm.auth.login.status_error', { status: err.status }));
        }
      } else {
        setErrorMessage(t('crm.auth.login.connection_error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main
      className="animate-fade-in relative flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="absolute right-6 top-6" data-testid="public-language-switcher-position">
        <LanguageSwitcher />
      </div>
      <Card
        style={{
          width: '100%',
          maxWidth: 440,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          {/* Та же марка, что в сайдбаре: 32×32 amber-плитка с «G» (M10). */}
          <div style={{ marginBottom: 12 }}>
            <AuthMark />
          </div>
          <Title
            level={3}
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: 'var(--ant-color-text)',
            }}
          >
            Govard CRM
          </Title>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {mode === 'login'
              ? t('crm.auth.login.subtitle')
              : t('crm.auth.login.register_subtitle')}
          </Text>
        </div>

        <Segmented<AuthMode>
          block
          value={mode}
          options={[
            { label: t('crm.auth.login.tab_login'), value: 'login' },
            { label: t('crm.auth.login.tab_register'), value: 'register' },
          ]}
          onChange={(value) => {
            setMode(value);
            setErrorMessage(null);
            // Переключение вкладки не должно стирать уже введённый email:
            // сбрасываем только пароль и имя, а заодно ошибки валидации.
            form.resetFields(['password', 'name']);
          }}
          style={{ marginBottom: 20 }}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => void onFinish(values)}
          data-testid="login-form"
        >
          {mode === 'register' ? (
            <Form.Item
              name="name"
              label={
                <Text strong style={{ color: 'var(--color-text-secondary)' }}>
                  {t('crm.auth.login.name')}
                </Text>
              }
              rules={[
                { required: true, message: t('crm.auth.login.name_required') },
                { max: 200, message: t('crm.auth.login.name_too_long') },
              ]}
            >
              <Input autoComplete="name" disabled={submitting} size="large" />
            </Form.Item>
          ) : null}

          <Form.Item
            name="email"
            label={
              <Text strong style={{ color: 'var(--color-text-secondary)' }}>
                {t('crm.auth.login.email')}
              </Text>
            }
            rules={[
              { required: true, message: t('crm.auth.login.email_required') },
              { type: 'email', message: t('crm.auth.login.email_invalid') },
            ]}
          >
            <Input
              id="email"
              type="email"
              autoComplete="email"
              disabled={submitting}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={
              <Text strong style={{ color: 'var(--color-text-secondary)' }}>
                {t('crm.auth.login.password')}
              </Text>
            }
            rules={[
              // Dev-mode вход работает по любому email без пароля (см. API
              // /auth/login); пароль обязателен только при регистрации.
              ...(mode === 'register'
                ? [
                    { required: true, message: t('crm.auth.login.password_required') },
                    { min: 8, message: t('crm.auth.login.password_min') },
                  ]
                : []),
            ]}
          >
            <Input.Password
              autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
              disabled={submitting}
              size="large"
            />
          </Form.Item>

          {errorMessage ? (
            <Alert type="error" title={errorMessage} showIcon style={{ marginBottom: 16 }} />
          ) : null}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              data-testid="login-submit"
              size="large"
              block
              loading={submitting}
            >
              {mode === 'login' ? t('crm.auth.login.submit') : t('crm.auth.login.create_account')}
            </Button>
            {/* Только в режиме входа: при регистрации пароля ещё нет. */}
            {mode === 'login' ? (
              <div className="mt-3 text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm transition-colors hover:text-amber-600"
                  style={{ color: 'var(--ant-color-text-tertiary)' }}
                >
                  {t('crm.auth.login.forgot_password')}
                </Link>
              </div>
            ) : null}
          </Form.Item>
        </Form>

        {/* Юридические ссылки доступны до входа: условия можно прочитать,
            ещё не имея аккаунта. */}
        <div
          className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
          data-testid="login-legal-links"
        >
          <Link
            to="/terms"
            className="text-xs transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.terms')}
          </Link>
          <Link
            to="/privacy"
            className="text-xs transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.privacy')}
          </Link>
          <Link
            to="/support"
            className="text-xs transition-colors hover:text-amber-600"
            style={{ color: 'var(--ant-color-text-tertiary)' }}
          >
            {t('crm.legal.link.support')}
          </Link>
        </div>
      </Card>
    </main>
  );
}
